export const ActiveCanvasUsers = {
  flow: 'x',
  state: {
    clients: {},
    users: []
  },
  alignSelf: 'center',
  fontSize: 'X',
  children: (el, s) => s.users,
  childExtends: 'Avatar',
  childProps: {
    avatarType: 'initials',
    opacity: 0.35,
    transition: 'B defaultBezier',
    transitionProperty: 'opacity, outline',
    data: {},
    ':hover': {
      opacity: 1
    },
    ':not(:last-child)': {
      margin: '0 -Z 0 0'
    },
    '.isActive': {
      zIndex: 2,
      opacity: 1,
      outline: '2px solid orange'
    },
    '.isFollowingYou': {
      zIndex: 2,
      opacity: 1,
      outline: '2px solid blue'
    },
    onClick: (ev, el) => {
      const { userId } = el
      el.call('setFollowingUser', userId)
      el.parent.update()
    },
    onDblclick: (ev, el) => {
      const { userId } = el
      el.call('askToFollow', userId)
    }
  },
  ':empty': {
    hide: true
  },
  onInit: async (el, st) => {
    const areClientsEqual = (prev = {}, next = {}) => {
      const prevKeys = Object.keys(prev)
      const nextKeys = Object.keys(next)

      if (prevKeys.length !== nextKeys.length) {
        return false
      }

      return prevKeys.every((key) => {
        const p = prev[key] || {}
        const n = next[key] || {}

        // Fallback: use map key as _id if field missing
        const pId = p._id || key
        const nId = n._id || key

        // Only compare properties that affect visual rendering
        return (
          pId === nId &&
          p.username === n.username &&
          p.avatar === n.avatar &&
          p.userId === n.userId &&
          p.followingUser === n.followingUser
        )
      })
    }

    el.sdk.rootBus.on('clients:updated', (data) => {
      if (!data) {
        return
      }

      const prevClients = st.clients || {}

      if (areClientsEqual(prevClients, data)) {
        return // nothing important changed
      }

      // Save new clients map; the stateDeps watcher derives the `users`
      // list from it. MERGE, never `replace` — a signal-store `replace`
      // DELETES every key absent from its argument (signal/store.js
      // storeReplace), so `replace({ clients })` also dropped `users` and
      // emptied the avatar strip on every presence message.
      st.update({
        clients: data
      })
    })
  },
  // ——— the presence watcher (WS-DEAD-ONSTATEUPDATE-HOOKS-SIBLINGS-1) ———
  //
  // `onStateUpdate` is a DECLARATIVE effect in DOMQL v3.14: the framework
  // registers it ONLY when a sibling `stateDeps` ARRAY exists, and no legacy
  // `stateUpdate` event is emitted. This block shipped with NO `stateDeps`
  // and the legacy `(changes, el)` signature, so the derived `users` list
  // was NEVER rebuilt — the `clients:updated` bus payload landed in state
  // and the avatar strip stayed on whatever it mounted with.
  //
  // Deps (each self-contained — FA204):
  //   • `clients` — the map the bus handler in `onInit` replaces.
  //   • root `followingUser` / `userId` — the two flags the derived rows
  //     carry (`isActive`, `isFollowingYou`); a follow change must repaint
  //     the outlines without a manual `el.parent.update()`.
  // NOT a dep: `users` — the handler WRITES it; watching it would re-enter.
  stateDeps: [
    (el, s) => (s || {}).clients,
    (el, s) => ((s && (s.root || s)) || {}).followingUser,
    (el, s) => ((s && (s.root || s)) || {}).userId
  ],
  // v3.14 signature: `(el, state, context, { prev, next })`.
  onStateUpdate: (el, s) => {
    const clients = (s && s.clients) || {}
    // Read the two follow flags off the SAME root the deps above select,
    // not via `getRootState()` — that helper resolves through a root-state
    // registry the effect has no guarantee about, and the effect must see
    // exactly the values it subscribed to.
    const { followingUser, userId } = (s && (s.root || s)) || {}

    const activeUsers = Object.keys(clients)
      .slice(0, 5)
      .map((id) => ({
        href: clients[id].route,
        userId: id,
        key: clients[id].username,
        title: clients[id].username,
        src: clients[id].avatar,
        isActive: followingUser === clients[id].userId,
        isFollowingYou: userId === clients[id]?.followingUser
      }))
      .filter((u) => u.title !== 'root')
      .filter((v, i, a) => a.findIndex((t) => t.userId === v.userId) === i)

    // MERGE the derived list — `replace` would delete `clients` (the key
    // this very effect watches) and immediately re-enter with an empty
    // map. `users` is deliberately NOT a dep, so this write starts no loop
    // (the legacy `preventStateUpdateListener` option is gone with the
    // legacy listener it named).
    el.state.update({
      users: activeUsers
    })
  }
}
